import {Linear, TweenLite} from "gsap";
import {AnimatedParticle, Emitter, PropertyNode} from "pixi-particles";
import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {BookOfShadowsGameConfig} from "../BookOfShadowsGameConfig";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {Orientation} from "@nolimitcity/slot-game/bin/core/stage/Orientation";

require("pixi-particles");

export class TweenParticleEmitter extends PIXI.Container {
    public currentTime: number = 0;
    private readonly _emitter: Emitter;
    private _totalDuration: number;
    private _prevTime: number = 0;
    private _emitterConfig: any;
    private _currentTween: TweenLite;

    constructor(textures: PIXI.Texture[], config: any, animatedParticles: boolean = false) {
        super();
        this._emitterConfig = config;
        if (animatedParticles) {
            this._emitter = new Emitter(this, [{
                framerate: 30,
                loop: true,
                hasLoaded: true,
                textures: textures
            }], config);
            this._emitter.particleConstructor = AnimatedParticle;
        } else {
            this._emitter = new Emitter(this, textures, config);
        }
        this._totalDuration = this._emitter.emitterLifetime + this._emitter.maxLifetime;
    }

    get emitter(): Emitter {
        return this._emitter;
    }

    public getAnimation(ease: any = Linear.easeNone): void {
        this._emitter.emit = false;
        this._emitter.cleanup();
        this.reset();
        this._emitter.emitterLifetime = this._emitterConfig.emitterLifetime ? this._emitterConfig.emitterLifetime : 0;
        this._totalDuration = this._emitter.emitterLifetime + this._emitter.maxLifetime;
        this._currentTween = new TweenLite(this, this._totalDuration,
            {
                currentTime: this._totalDuration,
                ease: ease,
                onStart: () => this.onStart(),
                onUpdate: () => this.onUpdate()
            });
    }

    private reset() {
        if (this._currentTween) {
            if (this._currentTween.isActive()) {
                this._currentTween.progress(1);
            }
            this._currentTween.kill();
        }
        this.currentTime = 0;
        this._prevTime = 0;
    }

    private onStart() {
        this._emitter.emit = true;
    }

    private onUpdate() {
        let delta: number = Math.abs(this.currentTime - this._prevTime);
        this._emitter.update(delta);
        this._prevTime = this.currentTime;
    }
}

export class MiniCoinShower extends BaseView {
    private _layer: ScalableLayer;
    private _coinParticles: TweenParticleEmitter;
    private readonly _miniCoinsConfigs: any = {
        "alpha": {
            "start": 1,
            "end": 1
        },
        "scale": {
            "start": 0.35,
            "end": 0.35,
            "minimumScaleMultiplier": 0.5
        },
        "color": {
            "start": "#ffffff",
            "end": "#ffffff"
        },
        "speed": {
            "start": 700,
            "end": 350,
            "minimumSpeedMultiplier": 1.3
        },
        "acceleration": {
            "x": 0,
            "y": 1500
        },
        "maxSpeed": 0,
        "startRotation": {
            "min": -110,
            "max": -70
        },
        "noRotation": false,
        "rotationSpeed": {
            "min": -400,
            "max": 400
        },
        "lifetime": {
            "min": 2.1,
            "max": 2.1
        },
        "blendMode": "normal",
        "frequency": 0.002,
        "emitterLifetime": 0.15,
        "maxParticles": 30,
        "pos": {
            "x": 0,
            "y": 0
        },
        "addAtBack": false,
        "spawnType": "rect",
        "spawnRect": {
            "x": -100,
            "y": -25,
            "w": 200,
            "h": 50
        }
    };

    /**
     * @description To play the shower.
     */
    public playMiniShower(): void {
        this._coinParticles.getAnimation();
    }

    /**
     * @inheritDoc
     */
    protected initAnimations() {
        super.initAnimations && super.initAnimations();

        this._layer = StageManager.getLayer(BookOfShadowsGameConfig.instance.LAYERS.BIG_WIN.name);

        const coinParticleTextures: PIXI.Texture[] = GameResources.getTextures("bigWinCoin0");

        this._coinParticles = new TweenParticleEmitter(coinParticleTextures, this._miniCoinsConfigs);

        this._layer.addChild(this._coinParticles);
    }

    /**
     * @inheritDoc
     * @param resizeData
     */
    protected onResize(resizeData: IResizeData): void {
        super.onResize && super.onResize(resizeData);
        const y: number = Math.round((resizeData.height - this._layer.y) / this._layer.scale.y) + 40;
        const levelSpeed: number = (resizeData.orientation === Orientation.PORTRAIT) ? 1450 : 1000;

        this._coinParticles.y = y;
        this._coinParticles.x = 360;
        this._coinParticles.emitter.startSpeed = new PropertyNode(levelSpeed, levelSpeed);
    }
}